import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Wand2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Alert, Badge } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { AiButton } from './Assistant';

interface Suggestion {
  criterionId: string;
  criterion: string;
  score: number;
  rationale: string;
  passages: Array<{ text: string; found: boolean }>;
}
interface Prefill {
  runId: string;
  documents: Array<{ title: string; version: number; coverage: { parts: number; total: number } }>;
  suggestions: Suggestion[];
}

/**
 * Suggested scores for one vendor from its own materials. A suggestion becomes a score only
 * when the evaluator uses it; the saved score then records the run.
 */
export function PrefillScores({
  program,
  categoryUrl,
  vendor,
  current,
  onUse,
}: {
  program: Program;
  categoryUrl: string;
  vendor: { id: string; name: string };
  current: (criterionId: string) => number | null;
  onUse: (criterionId: string, score: number, runId: string) => void;
}) {
  const toast = useToast();
  const [result, setResult] = useState<Prefill | null>(null);
  const [used, setUsed] = useState<Set<string>>(new Set());
  const run = useMutation({
    mutationFn: () => api.post<Prefill>(`${categoryUrl}/vendors/${vendor.id}/ai/prefill`),
    onSuccess: (r) => {
      setResult(r);
      setUsed(new Set());
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const partial = result?.documents.some((d) => d.coverage.parts < d.coverage.total);

  return (
    <>
      <AiButton program={program} tier={3} onClick={() => run.mutate()} loading={run.isPending}>
        <Wand2 /> Suggest
      </AiButton>
      <Dialog
        open={!!result}
        onClose={() => setResult(null)}
        wide
        title={`Suggested scores: ${vendor.name}`}
        description="From the vendor's own proposal and demo notes. Suggestions do not count until you use one; your score is yours to decide."
        footer={
          <Button variant="secondary" onClick={() => setResult(null)}>
            Close
          </Button>
        }
      >
        {result && (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted">
              Read: {result.documents.map((d) => `${d.title} (v${d.version})`).join(', ')}
            </p>
            {partial && (
              <Alert tone="warn" title="Long materials">
                Only the first parts of the longer documents were read.
              </Alert>
            )}
            {!result.suggestions.length && <p className="text-muted">The materials do not address these criteria.</p>}
            {result.suggestions.map((x) => {
              const now = current(x.criterionId);
              return (
                <div key={x.criterionId} className="rounded-md border border-border p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="font-medium">{x.criterion}</span>
                    <Badge tone="info">Suggested {x.score}</Badge>
                    {now !== null && <span className="text-xs text-muted">Your score: {now}</span>}
                    <span className="ml-auto">
                      {used.has(x.criterionId) ? (
                        <Badge tone="ok">
                          <Check className="mr-1 inline size-3" /> Used
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => {
                            onUse(x.criterionId, x.score, result.runId);
                            setUsed(new Set([...used, x.criterionId]));
                          }}
                        >
                          Use {x.score}
                        </Button>
                      )}
                    </span>
                  </div>
                  <p>{x.rationale}</p>
                  {x.passages.map((p, i) => (
                    <blockquote key={i} className="mt-1.5 border-l-2 border-border pl-2 text-[12.5px] text-muted">
                      {p.text}
                      {!p.found && (
                        <span className="ml-1 text-warn">(not found in the documents; check before relying on it)</span>
                      )}
                    </blockquote>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </Dialog>
    </>
  );
}
